import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PortalShell } from "@/pages/portal/PortalShell";
import { Chat } from "@/components/portal/Chat";
import { CallsPanel } from "@/components/portal/CallsPanel";
import { DocVault } from "@/components/portal/DocVault";
import { Sparkles, Plus, CheckCircle2 } from "lucide-react";

const COPILOT_TASKS = [
  { key: "summarize", label: "Summarize startup" },
  { key: "pending_docs", label: "Pending documents" },
  { key: "checklist", label: "Next-actions checklist" },
  { key: "customer_questions", label: "Customer questions" },
  { key: "draft_update", label: "Draft customer update" },
  { key: "escalation_note", label: "Escalation note" },
];

const STEP_STATUS_CLS = {
  done: "border-[#00E676]/40 text-[#00E676]",
  in_progress: "border-[#FF8800]/40 text-[#FF8800]",
  pending: "border-slate-600/60 text-slate-500",
};

function DocRequestForm({ orderId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", reason: "", required: true, due_date: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/portal/specialist/doc-requests", { ...form, order_id: orderId });
      toast.success("Document requested from founder");
      setForm({ name: "", reason: "", required: true, due_date: "" });
      setOpen(false);
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="doc-request-open" className="border-white/15 text-slate-300 hover:bg-white/5">
        <Plus className="w-3.5 h-3.5 mr-1.5" /> Request Document
      </Button>
    );
  }

  return (
    <form onSubmit={submit} className="glass rounded-xl p-4 space-y-3" data-testid="doc-request-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-slate-300 text-xs">Document Name *</Label>
          <Input data-testid="doc-request-name" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Bank Statement" className="bg-[#06080E] border-white/10 h-9 text-sm" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-slate-300 text-xs">Due Date</Label>
          <Input data-testid="doc-request-due" type="date" value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} className="bg-[#06080E] border-white/10 h-9 text-sm" />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-slate-300 text-xs">Reason / Instructions</Label>
        <Textarea data-testid="doc-request-reason" rows={2} value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} className="bg-[#06080E] border-white/10 text-sm" />
      </div>
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={saving} data-testid="doc-request-submit" className="bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold">
          {saving ? "Sending..." : "Send Request"}
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={() => setOpen(false)} className="border-white/15 text-slate-300">Cancel</Button>
      </div>
    </form>
  );
}

function OrderWorkspace({ order, userId, onChanged }) {
  const [copilot, setCopilot] = useState({ loading: "", reply: "" });
  const [panel, setPanel] = useState("workflow");

  const updateStep = async (index, status) => {
    try {
      await api.put(`/portal/specialist/orders/${order.id}/step`, { step_index: index, status });
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const runCopilot = async (task) => {
    setCopilot({ loading: task, reply: "" });
    try {
      const res = await api.post("/portal/specialist/copilot", { order_id: order.id, task });
      setCopilot({ loading: "", reply: res.data.reply });
    } catch (err) {
      toast.error(formatApiError(err));
      setCopilot({ loading: "", reply: "" });
    }
  };

  return (
    <div className="space-y-5" data-testid="order-workspace">
      <div className="glass rounded-xl p-5">
        <div className="flex flex-wrap items-center gap-2.5 mb-2">
          <p className="font-display text-lg font-bold text-white">{order.service_name}</p>
          <span className="text-[10px] font-mono-tech px-2.5 py-1 rounded-full border border-[#00B0FF]/40 text-[#00B0FF] uppercase">{order.status.replace(/_/g, " ")}</span>
        </div>
        <p className="text-sm text-slate-400">
          Startup: <span className="text-slate-200">{order.startup_name}</span> · Founder: <span className="text-slate-200">{order.founder_name}</span>
          {order.startup_score != null && <span className="text-[#FF8800]"> · AI Score {order.startup_score}/100</span>}
        </p>
        <p className="text-xs text-slate-500 mt-1">₹{order.total?.toLocaleString("en-IN")} · {order.processing_days || ""}</p>
      </div>

      <div className="flex gap-2 flex-wrap" role="tablist">
        {["workflow", "documents", "chat", "calls", "copilot"].map((p) => (
          <button
            key={p}
            data-testid={`workspace-tab-${p}`}
            onClick={() => setPanel(p)}
            className={`px-4 py-2 rounded-lg text-xs font-semibold capitalize transition-colors ${
              panel === p ? "bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E]" : "glass text-slate-400 hover:text-white"
            }`}
          >
            {p === "copilot" ? "AI Copilot" : p}
          </button>
        ))}
      </div>

      {panel === "workflow" && (
        <div className="space-y-2.5" data-testid="workflow-steps">
          {(order.workflow || []).map((w, i) => (
            <div key={i} className="glass rounded-xl px-4 py-3.5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`workflow-step-${i}`}>
              <span className="font-mono-tech text-[10px] text-slate-500 w-6 shrink-0">{String(i + 1).padStart(2, "0")}</span>
              <p className={`flex-1 text-sm ${w.status === "done" ? "text-slate-500 line-through" : "text-white"}`}>{w.name}</p>
              <span className={`text-[10px] font-mono-tech px-2 py-0.5 rounded-full border shrink-0 ${STEP_STATUS_CLS[w.status]}`}>
                {w.status.replace(/_/g, " ").toUpperCase()}
              </span>
              <div className="flex gap-2 shrink-0">
                {w.status !== "done" && (
                  <>
                    {w.status === "pending" && (
                      <Button size="sm" variant="outline" data-testid={`step-start-${i}`} onClick={() => updateStep(i, "in_progress")} className="border-[#FF8800]/30 text-[#FF8800] hover:bg-[#FF8800]/10 h-8 text-xs">
                        Start
                      </Button>
                    )}
                    <Button size="sm" variant="outline" data-testid={`step-done-${i}`} onClick={() => updateStep(i, "done")} className="border-[#00E676]/30 text-[#00E676] hover:bg-[#00E676]/10 h-8 text-xs">
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Done
                    </Button>
                  </>
                )}
                {w.date && <span className="text-[10px] text-slate-600 font-mono-tech self-center">{w.date}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {panel === "documents" && (
        <div className="space-y-4" data-testid="workspace-documents">
          <DocRequestForm orderId={order.id} onCreated={onChanged} />
          <DocVault startupId={order.startup_id} canReview testid="workspace-docvault" />
        </div>
      )}

      {panel === "chat" && <Chat orderId={order.id} testid="specialist-chat" />}

      {panel === "calls" && <CallsPanel orderId={order.id} myId={userId} testid="specialist-calls" />}

      {panel === "copilot" && (
        <div className="glass rounded-xl p-5" data-testid="copilot-panel">
          <p className="font-mono-tech text-[10px] tracking-[0.3em] text-[#FF8800] uppercase mb-4 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> Specialist Copilot
          </p>
          <div className="flex flex-wrap gap-2 mb-4">
            {COPILOT_TASKS.map((t) => (
              <button
                key={t.key}
                data-testid={`copilot-${t.key}`}
                onClick={() => runCopilot(t.key)}
                disabled={!!copilot.loading}
                className="glass px-4 py-2 rounded-full text-xs text-slate-300 hover:text-white hover:border-[#FF8800]/50 transition-colors"
              >
                {t.label}
              </button>
            ))}
          </div>
          {copilot.loading && <p className="text-xs text-slate-500 animate-pulse">Copilot is working...</p>}
          {copilot.reply && (
            <div className="rounded-xl border border-white/10 bg-[#06080E] p-4 text-sm text-slate-200 leading-relaxed whitespace-pre-wrap" data-testid="copilot-reply">
              {copilot.reply}
            </div>
          )}
          <p className="mt-3 text-[10px] text-slate-600 font-mono-tech">AI assistance only — the specialist remains responsible for professional decisions.</p>
        </div>
      )}
    </div>
  );
}

export default function SpecialistDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    api.get("/portal/specialist/orders").then((r) => setOrders(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/portal/auth/me").then((r) => {
      if (r.data.role !== "specialist") { navigate("/portal"); return; }
      setUser(r.data);
      load();
    }).catch(() => navigate("/portal"));
  }, [navigate, load]);

  if (!user) {
    return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 rounded-full border-2 border-[#FF8800] border-t-transparent animate-spin" /></div>;
  }

  const current = orders.find((o) => o.id === selected) || orders[0];
  const active = orders.filter((o) => o.status !== "completed");
  const completed = orders.filter((o) => o.status === "completed");

  return (
    <PortalShell user={user} title={`${user.specialist_category || "Specialist"} Dashboard`}>
      <div data-testid="specialist-dashboard">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-white" data-testid="specialist-welcome">
          Welcome, <span className="text-gradient-saffron">{user.name}</span>
        </h1>
        <p className="mt-1.5 text-sm text-slate-400">{user.specialist_category} · {active.length} active work order{active.length === 1 ? "" : "s"} · {completed.length} completed</p>

        {orders.length === 0 ? (
          <div className="glass rounded-2xl p-14 text-center text-slate-400 mt-8" data-testid="specialist-empty">
            No work orders assigned yet. LaunchLab admin will assign startups to you.
          </div>
        ) : (
          <div className="grid lg:grid-cols-[300px_1fr] gap-6 mt-8">
            <div className="space-y-2.5" data-testid="specialist-orders">
              {orders.map((o) => (
                <button
                  key={o.id}
                  data-testid={`specialist-order-${o.id}`}
                  onClick={() => setSelected(o.id)}
                  className={`w-full text-left glass rounded-xl px-4 py-3.5 transition-colors ${current?.id === o.id ? "border-[#FF8800]/50" : "hover:border-white/20"}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-white truncate">{o.service_name}</p>
                    <span className={`text-[9px] font-mono-tech px-2 py-0.5 rounded-full border shrink-0 ${
                      o.status === "completed" ? "border-[#00E676]/40 text-[#00E676]" : "border-[#FF8800]/40 text-[#FF8800]"
                    }`}>{o.status.replace(/_/g, " ").toUpperCase()}</span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1 truncate">{o.startup_name} · {o.founder_name}</p>
                </button>
              ))}
            </div>
            <div>{current && <OrderWorkspace order={current} userId={user.id} onChanged={load} />}</div>
          </div>
        )}
      </div>
    </PortalShell>
  );
}
